import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useMatch } from 'react-router';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import useMarkSessionRead from '@/hooks/conversations/use-mark-session-read';
import { allChatSessionQueryOptions, useSessionUnreadCount } from '@/hooks/sessions/use-conversation-sessions';
import useSessionSettings from '@/hooks/sessions/use-session-settings';
import type { ConversationType } from '@/models/conversations/conversation-session';
import ROUTES from '@/routes';
import { useIsDesktop } from '../use-user-platform';

export default function useSessionEffect(type: ConversationType, id: number) {
  const { dispatch } = useCommDispatcher();
  const { markAllRead } = useMarkSessionRead();
  const { session } = useSessionSettings(type, id);
  const queryClient = useQueryClient();
  const isDesktop = useIsDesktop();

  const match = useMatch(
    type === 'session'
      ? ROUTES.MESSAGES.SESSIONS.$buildPath({ params: { id } })
      : ROUTES.MESSAGES.GROUPS.$buildPath({ params: { id } }),
  );

  useSessionUnreadCount(true);

  useEffect(() => {
    if (!session) return;
    const isGroup = session.SessionType === 'group';
    const lastReadId = isGroup ? session.ReadGroupChatID : session.ReadChatID;
    markAllRead(type, id, session.LastMsgID, lastReadId, 'session-auto');

    // NOTE: 桌機版在聊天室內若卷軸不在底部，新訊息會產生未讀數，
    //       但因仍在當前聊天室內，不會觸發 addUnreadCount 更新本地 UnreadCount。
    //       因此需在切換聊天室時透過 API 取得遠端 UnreadCount 來更新。
    if (isDesktop) queryClient.invalidateQueries(allChatSessionQueryOptions);

    // NOTE: 桌機版在聊天室內若卷軸不在底部，新訊息會產生未讀數，
    //       但因仍在當前聊天室內，不會觸發 addUnreadCount 更新本地 UnreadCount。
    //       因此需在切換聊天室時透過 API 取得遠端 UnreadCount 來更新。
    // NOTE: 先移除此行為 @nelson.liu
    // if (isDesktop) queryClient.invalidateQueries(allChatSessionQueryOptions);
  }, [type, id]);

  useEffect(() => {
    if (match) {
      dispatch({
        event: 'session',
        data: { type, id },
      });

      return () => {
        dispatch({
          event: 'exit-session',
          data: { type, id },
        });
      };
    }
  }, [type, id, match]);
}
